/**
 * FieldFinder — the SHARED `<select>`-SSoT field lifecycle both finder views compose (D1).
 *
 * The two finder surfaces (homepage `DropdownView`, modal/catalog `ButtonColumnView`)
 * historically duplicated the per-level field mechanics — enhance/populate/reset/
 * applyLabel and the model-image+CTA mount — with subtle divergences. That drift
 * produced regressions (a) (image mis-mounted after row 1) and (c) (modal dropdown
 * left stale downstream combobox text). This collaborator owns those mechanics ONCE:
 *
 *  - the `<select>` → combobox enhance + change wiring (`enhanceField`);
 *  - option populate + combobox rebuild (`populateField`);
 *  - the ONE `resetField` that clears the `<select>` AND the enhanced combobox
 *    `input.value` + closes the list — fixing regression (c) for BOTH modes (§5.2);
 *  - the per-position label application (`applyLabel`);
 *  - the per-position enhancer teardown (`dropField`);
 *  - the model-field detail populate (`mountModelDetail`) + clear (`clearModelDetail`)
 *    + the vertical out-of-view reveal (`revealModelField`).
 *
 * It does NOT own the positional reconcile (that is `PositionalShellView`), the
 * column track / peek / in-button expansion (column-only, in `ButtonColumnView`),
 * or the decorative hero (homepage-only, in `DropdownView`). The *placement* of the
 * model detail is the caller's (column: the selected leaf cell; dropdown: the model
 * field's own detail box); FieldFinder only shares the populate + reveal.
 *
 * @see docs/speed-optimization/research/2026-06-07 - model-finder-column-plan-and-modal/research/unified-field-template-and-finder.md §5
 * @see docs/decisions/0010-model-finder-modal-responsive-column-rendering.md
 */

import type { DropdownEnhancer } from '../../../src-components/dropdown/dropdown-enhancer';
import { transform, passthroughStrategy } from '../../responsive-image';
import type { ScrollInView } from './scroll-in-view';
import { nearestScrollContainer } from './scroll-in-view';
import type { Node } from '../data/wire';
import type { LevelLabel } from './view';
import {
  isTransformableTileSrc,
  readMfTileOrigin,
  readMfTileSpec,
} from '../responsive-spec';

/** Where a field's model detail renders + how it is revealed (mode-specific placement). */
export interface FieldDetailTarget {
  /** The inert detail anchor to populate (column: the selected cell's; dropdown: the field's). */
  detail: HTMLAnchorElement | null;
  /** The element the vertical reveal scrolls into view (the model field box / the cell). */
  revealTarget: HTMLElement | null;
}

export interface FieldFinderOptions {
  /** The backend "open model" label (D13 — no hardcoded German). */
  openModelLabel: string;
  /** Reveal margin (px) off the viewport/container edge. */
  revealMargin: number;
  /** Mark/clear an image wrapper's transient loading state (shared with the view's manager). */
  setLoading: (wrapper: HTMLElement, on: boolean) => void;
}

/**
 * The shared `<select>`-SSoT field lifecycle for ONE view instance.
 *
 * The `DropdownEnhancer` is supplied lazily (`setEnhancer`): the homepage always has
 * one; the column view builds one only on the breakpoint-cross into dropdown mode and
 * tears it down above the breakpoint (Info Gap §11.3 — the view owns enhancer lifetime,
 * FieldFinder reads the current one). The `ScrollInView` is likewise supplied once it
 * exists (`setScrollInView`), so the reveal stays a no-op until a scroll context is set.
 */
export class FieldFinder {
  private enhancer: DropdownEnhancer | null = null;
  private scrollInView: ScrollInView | null = null;
  private readonly openModelLabel: string;
  private readonly revealMargin: number;
  private readonly setLoading: (wrapper: HTMLElement, on: boolean) => void;

  constructor(options: FieldFinderOptions) {
    this.openModelLabel = options.openModelLabel;
    this.revealMargin = options.revealMargin;
    this.setLoading = options.setLoading;
  }

  /** Hand the view's current enhancer (or null when destroyed above the breakpoint). */
  setEnhancer(enhancer: DropdownEnhancer | null): void {
    this.enhancer = enhancer;
  }

  /** Hand the view's vertical reveal mechanism once it is built (column layout / SSR ctor). */
  setScrollInView(scrollInView: ScrollInView | null): void {
    this.scrollInView = scrollInView;
  }

  // -- field <select> SSoT lifecycle ---------------------------------------

  /**
   * Enhance a field's `<select>` to a combobox + wire its change → `onChange(position)`.
   * Idempotent per position is the enhancer's concern; the caller guards re-enhance.
   */
  enhanceField(
    select: HTMLSelectElement,
    position: number,
    onChange: (position: number) => void
  ): void {
    this.enhancer?.enhance(select, position);
    select.addEventListener('change', () => onChange(position));
  }

  /** Populate a position's `<select>` options from items + rebuild the combobox list. */
  populateField(position: number, select: HTMLSelectElement, items: Node[]): void {
    items.forEach((item) => select.appendChild(this.createOption(item)));
    const dropdown = this.enhancer?.dropdowns.get(position);
    if (dropdown) this.enhancer!.rebuild(position, select);
  }

  /**
   * THE one reset (fixes regression c): clears the `<select>` options back to the
   * placeholder + value '', AND clears the enhanced combobox `input.value`, AND
   * closes the list. Identical for both modes — both views' `resetShell` calls this,
   * so neither can drift into leaving stale downstream combobox text.
   */
  resetField(position: number, select: HTMLSelectElement): void {
    while (select.options.length > 1) select.remove(1);
    select.value = '';
    select.disabled = false;
    const dropdown = this.enhancer?.dropdowns.get(position);
    if (dropdown) {
      dropdown.input.value = '';
      this.enhancer!.closeList(position);
    }
  }

  /** Apply a position's label to the `<select>` placeholder + combobox input + clear-label. */
  applyLabel(position: number, select: HTMLSelectElement, label: LevelLabel): void {
    const placeholder = Array.from(select.options).find((o) => !o.value);
    if (placeholder) placeholder.textContent = label.selectLabel;
    const dropdown = this.enhancer?.dropdowns.get(position);
    if (dropdown) dropdown.input.placeholder = label.selectLabel;
    this.enhancer?.setClearLabel(position, label.clearLabel);
  }

  /** Tear down a position's enhancer widget (close + delete). Called from `removeShell`. */
  dropField(position: number): void {
    if (this.enhancer?.dropdowns.has(position)) {
      this.enhancer.closeList(position);
      this.enhancer.dropdowns.delete(position);
    }
  }

  // -- model-field detail (shared populate; placement is the caller's) ------

  /**
   * Populate + reveal a model-field detail anchor (image load + backend label + href),
   * then — only on a LIVE selection whose reveal target is out of view — scroll it
   * into view. The image cell collapses cleanly when the leaf has no image. This is the
   * merge of the former `DropdownView.mountBottomImage` + `ButtonColumnView.populateDetail`
   * (one mechanism, two placements — §6.1).
   */
  mountModelDetail(target: FieldDetailTarget, node: Node, url: string, live: boolean): void {
    this.populateDetail(target.detail, node, url);
    this.revealModelField(target.revealTarget, live);
  }

  /** Hide + reset a detail anchor to inert (image cleared, href neutralised). */
  clearModelDetail(detail: HTMLAnchorElement | null): void {
    if (!detail) return;
    detail.classList.add('datte-mf-page-hidden');
    detail.classList.remove('is-loading');

    const wrapper = detail.querySelector<HTMLElement>('.datte-mf-page-detail-image-wrapper');
    const img = detail.querySelector<HTMLImageElement>('.datte-mf-page-detail-image');
    if (wrapper) {
      wrapper.classList.add('datte-mf-page-hidden');
      this.setLoading(wrapper, false);
    }
    if (img) {
      img.src = '';
      img.alt = '';
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      delete img.dataset.datteRawSrc;
    }
    detail.href = '#';
  }

  // -- internals ------------------------------------------------------------

  /** Populate a detail anchor (anchor href + image cell + backend label). Shared (DRY). */
  private populateDetail(detail: HTMLAnchorElement | null, node: Node, url: string): void {
    if (!detail) return;
    const wrapper = detail.querySelector<HTMLElement>('.datte-mf-page-detail-image-wrapper');
    const img = detail.querySelector<HTMLImageElement>('.datte-mf-page-detail-image');
    const label = detail.querySelector<HTMLElement>('.datte-mf-page-open-model');

    // The image cell collapses cleanly when the leaf has no image (~53% of leaves).
    if (node.imageUrl && img && wrapper) {
      wrapper.classList.remove('datte-mf-page-hidden');
      this.loadImage(img, wrapper, node.imageUrl, node.name);
    } else if (wrapper) {
      wrapper.classList.add('datte-mf-page-hidden');
      this.setLoading(wrapper, false);
    }

    detail.href = url;
    if (label) label.textContent = this.openModelLabel;
    detail.classList.remove('datte-mf-page-hidden', 'is-loading');
  }

  /**
   * Reveal the model field on a LIVE selection when it is out of view (D12 parity with
   * the horizontal peek). Never on restore/deep-link, never when already visible, never
   * before a scroll context exists.
   */
  private revealModelField(revealTarget: HTMLElement | null, live: boolean): void {
    if (!live || !revealTarget || !this.scrollInView) return;
    this.scrollInView.request({
      axis: 'y',
      target: revealTarget,
      container: nearestScrollContainer(revealTarget),
      margin: this.revealMargin,
    });
  }

  /**
   * Load the leaf image into its cell with a transient loading state (D13),
   * then size a composed (absolute-https) source responsively via the
   * server-provided region spec (§8; the ADR-0015 transform pattern).
   *
   * `data-datte-raw-src` remembers which RAW source the img currently shows:
   * after `transform()` rewrites `src` to a CF URL, the raw string is the only
   * stable identity for the "same image re-mounted" fast path.
   */
  private loadImage(img: HTMLImageElement, wrapper: HTMLElement, src: string, alt: string): void {
    const done = (): void => {
      this.setLoading(wrapper, false);
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
    };
    img.alt = alt;
    if (img.dataset.datteRawSrc === src && img.complete) {
      this.setLoading(wrapper, false);
      return;
    }
    this.setLoading(wrapper, true);
    img.addEventListener('load', done);
    img.addEventListener('error', done);
    // A stale srcset from a previously mounted node must never survive a source
    // change: the browser would keep picking the OLD candidates (and transform()
    // itself skips any img that already carries a srcset).
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.dataset.datteRawSrc = src;
    if (img.src !== src) img.src = src;
    // D-8.1: only the composed tier (absolute https R2 tile) is transformed; the
    // relative /media/... bridge tier is served as-is (see responsive-spec.ts).
    if (isTransformableTileSrc(src)) {
      // A CF-less context (a staging clone: PHP `RIMG_STRATEGY=passthrough` /
      // blank `baseUrl()`) emits NO `data-rimg-origin`, so `readMfTileOrigin`
      // returns undefined. There the CF `/cdn-cgi/image/` endpoint 404s AND the
      // composed tile lives on the Tailnet-only staging host the prod CF zone
      // cannot reach — either CF form breaks. Keep the already-set raw src by
      // transforming through the passthrough strategy. Prod (non-empty origin)
      // is unaffected: it still CF-transforms exactly as before.
      const origin = readMfTileOrigin(img);
      transform(
        wrapper,
        'img.datte-mf-page-detail-image',
        readMfTileSpec(img),
        origin !== undefined ? { origin } : { strategy: passthroughStrategy },
      );
    }
  }

  /** Build one native `<option>` from a tree node (id-as-value; name/url/image as data-*). */
  private createOption(item: Node): HTMLOptionElement {
    const opt = document.createElement('option');
    opt.value = String(item.id);
    opt.dataset.name = item.name;
    if (item.url) opt.dataset.url = item.url;
    if (item.imageUrl) opt.dataset.imageUrl = item.imageUrl;
    opt.textContent = item.name;
    return opt;
  }
}
